import React from "react";
import {Box, Link, Stack, Typography, useTheme} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {EmployeeBudget, ManagerStats, Overview} from "arteco-api-client-ts";
import {EmptyState, Panel, Tone, toneColor} from "../../../../components/kit";
import {dateEs, eur0, num1, pct1} from "./kpis";
import {pushReasons} from "../../../employee/productivity/emp_productivity_budgets";

interface Item {
	tone: Tone;
	title: string;
	detail: string;
	link: string;
	action: string;
}

const ORDER: Record<Tone, number> = {error: 0, warning: 1, info: 2, success: 3, neutral: 4};
const DAY = 86400000;

/** Lo que se sale de objetivo o espera una acción, de más a menos grave (#14497). */
export const attentionItems = (o: Overview, stats?: ManagerStats): Item[] => {
	const items: Item[] = [];
	const s = o.settings!;
	const c = o.current!;

	(o.lines || []).filter(l => (l.current?.margin || 0) < 0).forEach(l => items.push({
		tone: "error",
		title: `${l.name} pierde ${eur0(Math.abs(l.current!.margin || 0))} en ${c.label}`,
		detail: `Ingresa ${eur0(l.current!.revenue)} y sus nóminas cuestan ${eur0(l.current!.payrollCost)}.`,
		link: "#lineas", action: "Ver línea",
	}));

	(o.overdueByCustomer || []).forEach(v => {
		const days = v.oldestDueDate ? Math.round((new Date(o.today!.date + "T00:00:00").getTime() - new Date(v.oldestDueDate + "T00:00:00").getTime()) / DAY) : 0;
		items.push({
			tone: days > 60 ? "error" : "warning",
			title: `Vencido de ${v.customerName}: ${eur0(v.amount)} con IVA en ${v.count} ${v.count === 1 ? "factura" : "facturas"}`,
			detail: `La más antigua venció el ${dateEs(v.oldestDueDate)}, ${days === 0 ? "hoy" : `hace ${days} ${days === 1 ? "día" : "días"}`}.`,
			link: "/app/admin/billing/bill?overdue=1", action: "Ver vencidas",
		});
	});

	if ((c.topCustomerPct || 0) > (s.maxCustomerPct || 40)) {
		items.push({
			tone: "error",
			title: `${c.topCustomerName} es el ${pct1(c.topCustomerPct)} de la facturación`,
			detail: `El objetivo es que ningún cliente pase del ${num1(s.maxCustomerPct)} %.`,
			link: "/app/admin/billing/customer", action: "Ver clientes",
		});
	}
	if ((c.marginPct || 0) < (s.minMarginPct || 35)) {
		items.push({
			tone: (c.marginPct || 0) >= (s.minMarginPct || 35) - 5 ? "warning" : "error",
			title: `Margen bruto del ${pct1(c.marginPct)} en ${c.label}`,
			detail: `Por debajo del objetivo del ${num1(s.minMarginPct)} %.`,
			link: "#lineas", action: "Ver líneas",
		});
	}

	(stats?.budgets || []).forEach((b: EmployeeBudget) => {
		if ((b.deviationHours || 0) > 0) {
			items.push({
				tone: "warning",
				title: `${b.title} va ${num1(b.deviationHours)} h por encima de lo presupuestado`,
				detail: `${b.id} · ${b.customerName}: ${num1(b.dedicatedHours)} h de ${num1(b.plannedHours)}.`,
				link: `/app/admin/billing/budget/${b.id}`, action: "Ver presupuesto",
			});
		} else if (b.kind === "CUOTA" && (b.dedicatedHours || 0) > (b.plannedHours || 0) && (b.plannedHours || 0) > 0) {
			items.push({
				tone: "warning",
				title: `${b.title} supera las horas de su cuota`,
				detail: `${b.id} · ${b.customerName}: ${num1(b.dedicatedHours)} h de ${num1(b.plannedHours)} en el periodo en curso.`,
				link: `/app/admin/billing/budget/${b.id}`, action: "Ver presupuesto",
			});
		}
		// Dónde empujar (#14556): una entrada por presupuesto, con la razón más grave. Un exceso de horas ya sale
		// arriba: no se repite como «casi consumido».
		const reasons = pushReasons(b, new Date(o.today!.date + "T00:00:00"))
			.filter(r => !(r.code === "HIGH" && (b.deviationHours || 0) > 0));
		if (reasons.length > 0) {
			const tone = reasons.some(r => r.tone === "error") ? "error" : "warning";
			items.push({
				tone,
				title: `Empujar ${b.title} · ${reasons.map((r, i) => i === 0 ? r.text : r.text.charAt(0).toLowerCase() + r.text.slice(1)).join("; ")}`,
				detail: `${b.id} · ${b.customerName}: ${num1(b.dedicatedHours)} h de ${num1(b.ceilingHours ?? b.totalBudgetHours)}`
					+ (b.committedDeliveryDate && !b.deliveredDate ? `, entrega comprometida el ${dateEs(b.committedDeliveryDate)}.` : "."),
				link: `/app/admin/billing/budget/${b.id}`, action: "Ver presupuesto",
			});
		}
	});

	return items.sort((a, b) => ORDER[a.tone] - ORDER[b.tone]);
};

export const Attention = ({o, stats}: { o: Overview, stats?: ManagerStats }) => {
	const theme = useTheme();
	const items = attentionItems(o, stats);
	return (
		<Panel title="Qué requiere atención" subtitle="Lo que se sale de objetivo o espera una acción, de más a menos grave.">
			{items.length === 0
				? <EmptyState title="Nada fuera de objetivo" description="Todos los indicadores con objetivo lo cumplen y no hay vencidos, excesos de horas ni presupuestos que empujar."/>
				: <Box component="ul" sx={{listStyle: "none", m: 0, p: 0}}>
					{items.map((it, i) => (
						<Box component="li" key={i} sx={{display: "flex", gap: 1.5, py: 1.5, borderTop: i === 0 ? 0 : "1px solid", borderColor: "divider", pt: i === 0 ? 0 : 1.5}}>
							<Box sx={{width: 4, borderRadius: 1, flexShrink: 0, bgcolor: toneColor(theme, it.tone)}}/>
							<Box sx={{flex: 1, minWidth: 0}}>
								<Typography variant="body2" fontWeight={700}>{it.title}</Typography>
								<Typography variant="body2" color="text.secondary">{it.detail}</Typography>
							</Box>
							<Stack justifyContent="flex-start">
								{it.link.startsWith("#")
									? <Link href={it.link} underline="hover" fontWeight={700} variant="body2" sx={{whiteSpace: "nowrap"}}>{it.action}</Link>
									: <Link component={RouterLink} to={it.link} underline="hover" fontWeight={700} variant="body2" sx={{whiteSpace: "nowrap"}}>{it.action}</Link>}
							</Stack>
						</Box>
					))}
				</Box>}
		</Panel>
	);
};
